import {
  getFunctions,
  httpsCallable,
  type HttpsCallableResult,
} from 'firebase/functions'
import { initializeApp, getApps } from 'firebase/app'
import { auth } from './firebase'

/**
 * Stripe publishable key.
 * Safe to expose in frontend code — Stripe designed it that way.
 * The matching SECRET key lives only in Firebase Secret Manager.
 *
 * NOTE: Currently set to TEST mode for end-to-end checkout testing.
 * Live key (swap back when done testing):
 *   pk_live_51TUXYWQUGJBy0bOkvCc7KvHHJMcUjvdtMPg2eDIvonFKSm5CYRzJrk38FRIoRouXvcV3YMzHjJbtVuko03ubAeZt00ToicbZAk
 */
export const STRIPE_PUBLISHABLE_KEY =
  'pk_test_51TUXYWQUGJBy0bOkS85pxjWGrxIKgUDhZVcJUMrhouYOl9wORi7cDkimTOxcDHMIBkzx4DGmnOTL7DpVFl4tl1wh00AE395Ee0'

// Reuse the same Firebase app initialized in firebase.ts
function getApp() {
  return getApps()[0]
}

const functions = getFunctions(getApp())

export interface CheckoutLineItemInput {
  /** Stripe Price ID (price_...). Optional if `lookupKey` is provided. */
  priceId?: string
  /** Stripe price `lookup_key`. Server resolves to active price. Optional if `priceId` is provided. */
  lookupKey?: string
  quantity: number
}

export interface CreateCheckoutSessionRequest {
  lineItems: CheckoutLineItemInput[]
  successUrl: string
  cancelUrl: string
}

export interface CreateCheckoutSessionResponse {
  url: string
  sessionId: string
}

/**
 * Create a Stripe Checkout Session via the deployed Cloud Function.
 * The function lives at functions/createCheckoutSession in your Firebase project.
 *
 * Server-side, the function:
 *   - Verifies the Firebase Auth token (request.auth)
 *   - Looks up each priceId in Stripe to get authoritative pricing
 *   - Creates a Checkout Session with client_reference_id = uid
 *   - Returns the hosted Stripe Checkout URL
 */
export async function createCheckoutSession(
  payload: CreateCheckoutSessionRequest,
): Promise<CreateCheckoutSessionResponse> {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to check out.')
  }

  const callable = httpsCallable<
    CreateCheckoutSessionRequest,
    CreateCheckoutSessionResponse
  >(functions, 'createCheckoutSession')

  const result: HttpsCallableResult<CreateCheckoutSessionResponse> =
    await callable(payload)

  if (!result.data?.url) {
    throw new Error('No checkout URL returned. Please try again.')
  }

  return result.data
}

/**
 * Redirect the browser to a Stripe Checkout URL.
 * Stripe handles all payment UI from there.
 */
export function redirectToCheckout(url: string): void {
  window.location.href = url
}

// ─── Price Fetching ───────────────────────────────────────────────

export interface StripePriceInfo {
  /** Amount in cents */
  amount: number
  /** Lowercase ISO currency code, e.g. 'usd' */
  currency: string
}

/** Map keyed by Stripe price `lookup_key` */
export type StripePriceMap = Record<string, StripePriceInfo>

export interface FetchProductPricesRequest {
  lookupKeys: string[]
}

export interface FetchProductPricesResponse {
  prices: StripePriceMap
}

/**
 * Fetch current prices for a list of Stripe price `lookup_key`s.
 * Calls the deployed `getProductPrices` Cloud Function. Stripe is the single
 * source of truth — this lets the site reflect price changes (in either test
 * or live mode) without a code redeploy.
 */
export async function fetchProductPrices(
  lookupKeys: string[],
): Promise<StripePriceMap> {
  if (!lookupKeys.length) return {}

  const callable = httpsCallable<
    FetchProductPricesRequest,
    FetchProductPricesResponse
  >(functions, 'getProductPrices')

  const result = await callable({ lookupKeys })
  return result.data?.prices ?? {}
}
